import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { CheckSquare, DollarSign, FolderOpen, Share2, Trash2, Shield, Repeat, Package, Car, AlarmClock, Store } from 'lucide-react';

const modules = [
  { path: '/tasks', label: 'Tasks', icon: CheckSquare, desc: 'Manage your tasks & to-dos', color: 'bg-zinc-900 dark:bg-zinc-100' },
  { path: '/expenses', label: 'Expenses', icon: DollarSign, desc: 'Track your spending', color: 'bg-emerald-600' },
  { path: '/habits', label: 'Habits', icon: Repeat, desc: 'Build daily routines', color: 'bg-violet-600' },
  { path: '/vehicles', label: 'Vehicles', icon: Car, desc: 'Manage your vehicles', color: 'bg-cyan-600' },
  { path: '/assets', label: 'Assets', icon: Package, desc: 'Fixed assets & equipment', color: 'bg-amber-600' },
  { path: '/vendors', label: 'Vendors', icon: Store, desc: 'Manage vendors & contacts', color: 'bg-indigo-500' },
  { path: '/reminders', label: 'Reminders', icon: AlarmClock, desc: 'Upcoming alerts', color: 'bg-rose-500' },
  { path: '/workspaces', label: 'Workspaces', icon: FolderOpen, desc: 'Organize by workspace', color: 'bg-orange-500' },
  { path: '/shared', label: 'Shared', icon: Share2, desc: 'Shared records', color: 'bg-blue-600' },
  { path: '/trash', label: 'Trash', icon: Trash2, desc: 'Deleted records', color: 'bg-red-500' },
];

export default function HomePage() {
  const { user } = useAuth();
  const { projects } = useApp();
  const navigate = useNavigate();

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="home-page">
      <div className="mb-10">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Hi, {user?.display_name || user?.username}</h2>
        <p className="text-base text-zinc-500 mt-2">{projects.length} workspace{projects.length !== 1 ? 's' : ''} active</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {modules.map(m => (
          <button key={m.path} onClick={() => navigate(m.path)}
            className="group flex flex-col items-start p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl hover:shadow-lg hover:border-zinc-300 dark:hover:border-zinc-700 transition-all duration-200 text-left"
            data-testid={`home-${m.path.slice(1)}`}>
            <div className={`w-11 h-11 rounded-xl ${m.color} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
              <m.icon className="h-5 w-5 text-white dark:text-zinc-900" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-medium">{m.label}</span>
            <span className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{m.desc}</span>
          </button>
        ))}
        {['admin','super_admin'].includes(user?.role) && (
          <button onClick={() => navigate('/admin')}
            className="group flex flex-col items-start p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl hover:shadow-lg hover:border-zinc-300 transition-all duration-200 text-left"
            data-testid="home-admin">
            <div className="w-11 h-11 rounded-xl bg-violet-600 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <Shield className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-medium">Admin</span>
            <span className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Manage users & system</span>
          </button>
        )}
      </div>
    </div>
  );
}
